
import { Component, Vue } from 'nuxt-property-decorator'
import { IVueI18n } from 'vue-i18n'
import Cookies from 'js-cookie'
import { Container } from '@mg/micro/vue2/Container'
import FlashMessageToastContainer from '@mg/web-components/Atoms/FlashMessageToastContainer'
import { FeaturesConfig } from '@mg/web-components/types'
import scrollBehavior from '~/utils/router.scrollBehaviour'
import TheTracking from '~/components/TheTracking.vue'
import TheHeader from '~/components/header/TheHeader.vue'
import TheHeaderBasic from '~/components/header/TheHeaderBasic.vue'
import {
  apolloServerTranslationPage,
  defaultLanguage,
} from '~/utils/translationService'
import { headerConfig } from '~/config/headerConfig'
import { mapDeviceType, Resize, watchResize } from '~/plugins/resize'
import { GET_CONFIG } from '~/graphql/gql/config'
import {
  GQLConfig,
  GQLContent,
  GQLMenu,
  GQLPageBanners,
  GQLQuery,
  GQLQueryGetTranslationsArgs,
  GQLUser,
  GQLWishList,
} from '~/graphql/__generated__/interfaces'
import formatPriceFactory from '~/filters/formatPriceFactory'
import TheFooter from '~/components/header/TheFooter.vue'
import TheFooterBasic from '~/components/header/TheFooterBasic.vue'
import TheBackgroundBanner from '~/components/banners/TheBackgroundBanner.vue'
import {
  GET_CONTENT_BY_TITLE,
  GQLGetContentByTitleQueryInput,
} from '~/graphql/gql/content'
import {
  GET_PAGE_BANNERS,
  GET_POSITIONED_PAGE_BANNERS,
  GetPageBannersQuery,
  GetPositionedPageBannersQuery,
} from '~/graphql/gql/banners'
import { GQL_LOCAL_ACCOUNT } from '~/graphql/gqlLocal/localAccount'
import {
  GQLLocalAccount,
  GQLLocalHeader,
  GQLLocalUi,
} from '~/graphql/__generated__/interfacesLocal'
import webComponentsConfig from '~/config/webComponents'
import { setupEdeRolloutCookie } from '~/utils/setupEdeRolloutCookie'
import { isReviewApp } from '~/utils/testingBar'
import { GET_TRANSLATIONS } from '~/graphql/gql/translation'
import { GET_BASIC_USER_INFO } from '~/graphql/gql/user'
import { GET_WISH_LISTS } from '~/graphql/gql/wishList'
import { GQL_LOCAL_UI } from '~/graphql/gqlLocal/localUi'
import showVipFeatures from '~/utils/showVipFeatures'
import { ELEMENT_IDS } from '~/utils/elementIds'
import { GET_LOCAL_HEADER } from '~/graphql/gqlLocal/localHeader'
import { GET_MENU_ITEM_FULL } from '~/graphql/gql/menu'
import ROUTES from '~/config/routes'
import { getRouteDefinition } from '~/utils/routesGetDefinition'
import TheGlobalModalHeader from '~/components/modal/TheGlobalModalHeader.vue'
import TheAccountMenu from '~/components/TheAccountMenu.vue'
import ApolloFetchPolicies from '~/utils/apolloFetchPolicies'
import { getMenuVariables } from '~/utils/getMenuVariables'
import { logApolloError } from '~/utils/apolloErrors'

const ErrorAlert = () => import('~/components/Error.vue')
const TheGlobalModal = () => import('~/components/modal/TheGlobalModal.vue')
const VipNoticePanel = () => import('~/components/header/VipNoticePanel.vue')
const MPMessagePanel = () => import('~/components/header/MPMessagePanel.vue')
const TheLegalConsent = () => import('~/components/header/TheLegalConsent.vue')
const TheTestingBar = () => import('~/components/TheTestingBar.vue')
const TheReviewAppBanner = () =>
  import('~/components/TheReviewAppBanner.vue')
const TheHeaderInfoDynamic = () =>
  import('~/components/header/TheHeaderInfo.vue')
const TheHeaderInfoBanman = () =>
  import('~/components/header/TheHeaderInfoBanman.vue')

const GET_CONFIG_POLL_INTERVAL = 60 * 1000

@Component<LayoutDefault>({
  middleware: ['searchPageGuard'],
  apollo: {
    account: GQL_LOCAL_ACCOUNT,
    getConfig: {
      ...(process.client ? { pollInterval: GET_CONFIG_POLL_INTERVAL } : {}),
      error(e: any) {
        this.$logger.error(`DefaultLayout - getConfig query error : ${e}`, e)
      },
      query: GET_CONFIG,
      errorPolicy: 'none',
      result(result: { data: { getConfig: GQLConfig } }) {
        const getConfig = result?.data?.getConfig
        setupEdeRolloutCookie(getConfig, this.$shopConfig)
        formatPriceFactory(getConfig.shopConfig)
        webComponentsConfig(getConfig.shopConfig)
        return getConfig
      },
    },
    getPageBanners: {
      notifyOnNetworkStatusChange: true,
      query: GET_PAGE_BANNERS,
      variables(): GetPageBannersQuery {
        const pageUrl = this.$route.path
        return {
          pageUrl,
        }
      },
      error(e: any) {
        this.$logger.error(
          `DefaultLayout - getPageBanners query error : ${e}`,
          e
        )
      },
    },
    getTranslations: {
      fetchPolicy: ApolloFetchPolicies.CACHE_FIRST,
      query: GET_TRANSLATIONS,
      update(data: { getTranslations: GQLQuery['getTranslations'] }) {
        this._i18n.mergeLocaleMessage(defaultLanguage, data.getTranslations)
        return data
      },
      variables(): GQLQueryGetTranslationsArgs {
        return {
          namespaces: apolloServerTranslationPage(this.$route),
        }
      },
      error(e: any) {
        this.$logger.error(
          `DefaultLayout - getTranslations query error : ${e}`,
          e
        )
      },
    },
    getPositionedPageBanners: {
      skip() {
        const positions =
          getRouteDefinition(this.$route)?.bannerPrefetchPositions || []
        return positions.length === 0
      },
      query: GET_POSITIONED_PAGE_BANNERS,
      variables(): GetPositionedPageBannersQuery {
        const pageUrl = this.$route.path
        const positions =
          getRouteDefinition(this.$route)?.bannerPrefetchPositions || []

        return {
          isMobile: this.$resize.isMobile,
          pageUrl,
          query: {
            positions,
          },
        }
      },
      error(e: any) {
        this.$logger.error(
          `DefaultLayout - getPositionedPageBanners query error : ${e}`,
          e
        )
      },
    },
    ui: {
      query: GQL_LOCAL_UI,
    },
    user: {
      fetchPolicy: ApolloFetchPolicies.CACHE_ONLY,
      query: GET_BASIC_USER_INFO,
      skip() {
        return !this.$shopConfig.user.id && !this.account?.isLogged
      },
      error(e: any) {
        this.$logger.error(`DefaultLayout - user query error : ${e}`, e)
      },
    },
    wishLists: {
      query: GET_WISH_LISTS,
      skip() {
        return !this.$shopConfig.user.id && !this.account?.isLogged
      },
      error(e: any) {
        this.$logger.error(`DefaultLayout - wishLists query error : ${e}`, e)
      },
    },
    header: {
      query: GET_LOCAL_HEADER,
      error(e: any) {
        this.$logger.error(`DefaultLayout - header query error : ${e}`, e)
      },
    },
    getContentByTitle: {
      fetchPolicy: '',
      query: GET_CONTENT_BY_TITLE,
      skip: true,
      variables(): GQLGetContentByTitleQueryInput {
        return {
          title: 'web_header_information',
        }
      },
      error(e: any) {
        logApolloError(
          e,
          'DefaultLayout - getContentByTitle query error',
          this.$logger
        )
      },
    },
    getMenu: {
      query: GET_MENU_ITEM_FULL,
      variables() {
        return getMenuVariables(
          'web',
          this.$route.query.naviPreview as string,
          this.$shopConfig.abTests.abTestsData.menuAbTest,
          this.header?.menuIsVisible || this.isHomepage
        )
      },
      error(e: any) {
        this.$logger.error(`DefaultLayout - getMenu query error : ${e}`, e)
      },
    },
    getUserMenu: {
      query: GET_MENU_ITEM_FULL,
      update(data: { getMenu: GQLMenu }) {
        return data.getMenu
      },
      variables() {
        return getMenuVariables(
          'kc',
          this.$route.query.naviPreview as string,
          this.$shopConfig.abTests.abTestsData.menuAbTest,
          false
        )
      },
      error(e: any) {
        this.$logger.error(`DefaultLayout - getUserMenu query error : ${e}`, e)
      },
    },
    getHorizonMenu: {
      query: GET_MENU_ITEM_FULL,
      update(data: { getMenu: GQLMenu }) {
        return data.getMenu
      },
      variables() {
        return getMenuVariables(
          'horizon',
          this.$route.query.naviPreview as string,
          this.$shopConfig.abTests.abTestsData.menuAbTest,
          false
        )
      },
      error(e: any) {
        this.$logger.error(
          `DefaultLayout - getHorizonMenu query error : ${e}`,
          e
        )
      },
    },
  },
  components: {
    TheGlobalModalHeader,
    ErrorAlert,
    MPMessagePanel,
    TheBackgroundBanner,
    TheFooter,
    TheFooterBasic,
    TheGlobalModal,
    TheHeader,
    TheHeaderBasic,
    TheHeaderInfoBanman,
    TheHeaderInfoDynamic,
    TheLegalConsent,
    TheReviewAppBanner,
    TheTestingBar,
    TheTracking,
    VipNoticePanel,
    TheAccountMenu,
    Container,
    FlashMessageToastContainer,
  },
  head() {
    return headerConfig({
      getConfig: this.getConfig,
      isBot: this.$shopConfig.isBot,
      route: this.$route,
      translate: this.$t.bind(this),
    })
  },
})
export default class LayoutDefault extends Vue {
  getConfig: GQLConfig
  getContentByTitle: GQLContent
  getPageBanners!: GQLPageBanners
  account: GQLLocalAccount
  user: GQLUser
  wishLists: GQLWishList[]
  _i18n: IVueI18n
  ui: GQLLocalUi
  showVipFeatures = showVipFeatures
  ELEMENT_IDS = ELEMENT_IDS
  header: GQLLocalHeader

  errorCaptured(e: any) {
    console.log(e)
    this.$logger.error(`DefaultLayout - captured error - ${e}`, e)
    return false
  }

  created() {
    this.$Progress.start()
    if (process.client) {
      this.$pageLoad.start()
    }

    this.$router.beforeEach((to, from, next) => {
      if (from.name !== to.name) {
        this.$abTesting.clearActiveTestCookies()
        this.$abTesting.setMgTestsCookies()
      }

      if (!to.query.tab) {
        this.$Progress.start()
        if (process.client) {
          this.$pageLoad.start()
        }
      }
      if (process.client) {
        this.$errorPages!.hideError()
      }

      next()
    })
  }

  mounted() {
    this.$resize = mapDeviceType()
    if (!this.infoBanner && !this.getContentByTitle) {
      this.$apollo.queries.getContentByTitle.skip = false
      this.$apollo.queries.getContentByTitle.refetch()
    }
    watchResize((type: Resize) => {
      this.$resize = type
    })
    if (this.$route.query.scrollId || this.$route.hash) {
      scrollBehavior(this.$route).then(({ y, selector }) => {
        if (y) {
          window.scrollTo(0, y)
        } else if (selector) {
          const element = document.querySelector(selector)
          const offset = element?.getBoundingClientRect()?.top ?? 0
          window.scrollTo(0, offset)
        }
      })
      window.$nuxt.$nextTick(() => window.$nuxt.$emit('triggerScroll'))
    }
  }

  get featuresConfig(): FeaturesConfig {
    return this.$shopConfig.featuresConfig
  }

  get useBasicHeader(): boolean {
    return !!this.featuresConfig.useBasicHeader
  }

  get useBasicFooter(): boolean {
    return !!this.featuresConfig.useBasicFooter
  }

  get isLoggedIn(): boolean {
    return !!this.$shopConfig.user.id || !!this.account?.isLogged
  }

  get isHomepage(): boolean {
    return this.$route.name === ROUTES.homepage.name
  }

  get isMpUser(): boolean {
    return Boolean(this.user?.partnerId && this.ui?.mpPanelTranslationKey)
  }

  get isVipUser(): boolean {
    return this.showVipFeatures(this.account?.customer?.vipType)
  }

  get infoBanner() {
    return this.getPageBanners?.infoBanner
  }

  get backgroundBanner() {
    return this.getPageBanners?.backgroundBanner
  }

  get errorCode() {
    return this?.$errorPages?.errorCode || false
  }

  get isTestingBar(): boolean {
    return Cookies.get('SPA_TESTING_BAR') === '1'
  }

  get showReviewAppBanner(): boolean {
    return !!isReviewApp()
  }
}
